import { Button, Checkbox, FormControlLabel, Grid, IconButton, Snackbar, SnackbarCloseReason, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import { fetchApi } from 'core/fetchApi';

export type VehicleType = {
  VehicleTypeID: number;
  Name: string;
  BoxLength: number;
  BoxWidth: number;
  BoxHeight: number;
  PayLoad: number;
}

export type VehicleTypesResponse = {
  VehicleTypes: VehicleType[];
  SelectedVehicleTypeIDs: number[];
}

const _details = (vehicleType: VehicleType) => {
  const details: string[] = [];
  if (vehicleType.BoxLength || vehicleType.BoxWidth || vehicleType.BoxHeight)
    details.push(`${vehicleType.BoxLength} x ${vehicleType.BoxWidth} x ${vehicleType.BoxHeight} in.`);
  if (vehicleType.PayLoad)
    details.push(`${vehicleType.PayLoad.toLocaleString()} lbs.`);
  return details;
}

const VehicleTypes = () => {
  const [snackbar, setSnackbar] = useState('');
  const data = useLoaderData() as VehicleTypesResponse;
  const [selected, setSelected] = useState<number[]>(data.SelectedVehicleTypeIDs);

  const handleCloseSnackbar = (_: Event | React.SyntheticEvent, reason: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbar('');
  };

  const _toggle = (vehicleTypeID: number, checked: boolean) => {
    if (checked) {
      setSelected([...selected, vehicleTypeID]);
    } else {
      setSelected(selected.filter(x => x !== vehicleTypeID));
    }
  }

  const _toggleAll = (checked: boolean) => {
    setSelected(checked ? data.VehicleTypes.map(x => x.VehicleTypeID) : []);
  }

  const _save = () => {
    fetchApi<{ Success: boolean, ErrorMessage: string }>({
      url: 'VehicleTypes',
      method: 'POST',
      payload: {
        VehicleTypes: selected,
      }
    }).then((response) => {
      if (!response.Success) {
        alert(response.ErrorMessage);
      } else {
        setSnackbar('Successfully Saved!');
      }
    })
    .catch(() => {
      alert('Unknown error saving!');
    });
  }

  return <>
    <Typography variant="body2" sx={{ mb: 2 }}>
      Select the vehicle types you are able to haul. You will only be offered loads matching these types.
    </Typography>
    <FormControlLabel
      control={<Checkbox
        checked={selected.length === data.VehicleTypes.length}
        indeterminate={selected.length > 0 && selected.length < data.VehicleTypes.length}
        onChange={(e) => _toggleAll(e.target.checked)}
      />}
      label="Select All"
    />
    <Grid container spacing={1} sx={{ mt: 1 }}>
      {data.VehicleTypes.map((vehicleType) => <Grid key={vehicleType.VehicleTypeID} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <FormControlLabel
          sx={{ alignItems: 'flex-start', mr: 0 }}
          control={<Checkbox
            sx={{ pt: 0 }}
            checked={selected.indexOf(vehicleType.VehicleTypeID) > -1}
            onChange={(e) => _toggle(vehicleType.VehicleTypeID, e.target.checked)}
          />}
          label={<>
            {vehicleType.Name}
            {_details(vehicleType).map((detail) => <Typography key={detail} variant="caption" component="div" color="text.secondary" noWrap>
              {detail}
            </Typography>)}
          </>}
        />
      </Grid>)}
    </Grid>
    <br />
    <Button variant="contained" color="primary" onClick={_save}>Save</Button>
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      open={!!snackbar}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      message={snackbar}
      action={
        <IconButton size="small" aria-label="close" color="inherit" onClick={(e) => handleCloseSnackbar(e, 'timeout')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    />
  </>;
}

export default VehicleTypes;
